import React, { createContext, useReducer, useContext, useMemo } from "react";

const OrderContext = createContext();

export const STATUS_GROUPS = {
  COMPLETED: [
    "completed",
    "drop_off",
    "driver_canceled",
    "client_canceled",
    "provider_canceled",
    "assign_failed",
    "timedout",
  ],
  ACTIVE: ["started", "arrived", "in progress"],
  UPCOMING: ["scheduled", "scheduled_accepted"],
};

export const formatLocalDate = date =>
  new Date(date).toISOString().split("T")[0];

export const shouldRemoveOrder = (order, filter, type) => {
  const { status } = order;

  if (
    filter !== "all" &&
    filter !== "ACTIVE" &&
    filter !== "UPCOMING" &&
    filter !== "PAST"
  ) {
    if (type === "added") return status !== filter;
    if (type === "updated") return status !== filter;
    return false;
  }

  if (type === "added") {
    if (filter === "all") return false;
    if (filter === "UPCOMING")
      return !STATUS_GROUPS.UPCOMING.includes(status);
    if (filter === "ACTIVE")
      return !STATUS_GROUPS.ACTIVE.includes(status);
    if (filter === "PAST")
      return !STATUS_GROUPS.COMPLETED.includes(status);
  }

  if (type === "updated") {
    switch (filter) {
      case "all":
        return STATUS_GROUPS.COMPLETED.includes(status);
      case "ACTIVE":
        return !STATUS_GROUPS.ACTIVE.includes(status);
      case "UPCOMING":
        return !STATUS_GROUPS.UPCOMING.includes(status);
      case "PAST":
        return !STATUS_GROUPS.COMPLETED.includes(status);
      default:
        return false;
    }
  }

  return false;
};

export const sortOrdersByStatusAndTime = (orders, filter) => {
  if (filter === "PAST") return orders;

  const grouped = {
    started: [],
    arrived: [],
    "in progress": [],
    scheduled: [],
    scheduled_accepted: [],
    others: [],
  };

  for (const o of orders) {
    if (grouped[o.status]) grouped[o.status].push(o);
    else grouped.others.push(o);
  }

  const sortByDate = arr =>
    arr.sort((a, b) => new Date(a.schedule_date) - new Date(b.schedule_date));

  const upcomingCombined = [...grouped.scheduled, ...grouped.scheduled_accepted];

  if (filter === "ACTIVE" || filter === "all") {
    return [
      ...sortByDate(grouped.started),
      ...sortByDate(grouped.arrived),
      ...sortByDate(grouped["in progress"]),
      ...(filter === "all" ? sortByDate(upcomingCombined) : []),
    ];
  }

  if (filter === "UPCOMING") {
    return sortByDate(upcomingCombined);
  }

  return sortByDate(orders);
};

const orderReducer = (state, action) => {
  switch (action.type) {
    case "ADD_ORDER": {
      const remainingOrders = (state.orders || []).filter(o => o.id !== action.payload.id);
      
      const limitedOrders = remainingOrders.length >= 100 
        ? remainingOrders.slice(0, 99) 
        : remainingOrders;

      return {
        ...state,
        orders: [{ ...action.payload, showLoadingBar: true, type: "added" }, ...limitedOrders],
      };
    }

    case "FINALIZE_ORDER": {
      const { order, filter, type } = action.payload;

      if (shouldRemoveOrder(order, filter, type)) {
        return {
          ...state,
          orders: state.orders.filter(o => o.id !== order.id),
          movedTripId: order.id,
        };
      }

      const updatedOrders = state.orders.map(o =>
        o.id === order.id ? { ...o, showLoadingBar: type === "added" ? false : true, reposition: false } : o
      );

      const hasSingleOrder = state.orders.length === 1 && state.orders[0].id === order.id;
      const hasFewOrders = state.orders.length <= 9;

      const formatLocalDate = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return (
          d.getFullYear() +
          "-" +
          String(d.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(d.getDate()).padStart(2, "0")
        );
      };

      const payloadDate = formatLocalDate(order.schedule_date);
      const otherOrders = state.orders.filter(o => o.id !== order.id);
      const currentDates = otherOrders.map(o => formatLocalDate(o.schedule_date)).filter(Boolean);

      const sortedDates = [...currentDates].sort();
      const minDate = sortedDates[0];
      const maxDate = sortedDates[sortedDates.length - 1];

      const isTooFuture = payloadDate && maxDate && payloadDate > maxDate;
      const isTooOld = payloadDate && minDate && payloadDate < minDate;

      let finalOrders;

      if (hasSingleOrder) {
        // If only one order — just update it
        finalOrders = [{ ...order, showLoadingBar: type === "added" ? false : true, reposition: false }];
      } else if (hasFewOrders) {
        // if Few orders — and its new order push updated one to end
        const updatedOrder = updatedOrders.find(o => o.id === order.id);

        if (type === "added") {
          if (isTooFuture) {
            // If date future than current available max date add at last
            finalOrders = [
              ...updatedOrders.filter(o => o.id !== order.id),
              updatedOrder,
            ];
          } else {
            // else sort it
            finalOrders = sortOrdersByStatusAndTime(updatedOrders, filter);
          }
        } else {
          // else sort it
          finalOrders = sortOrdersByStatusAndTime(updatedOrders, filter);
        }
      } else {
        // else sort it
        if (isTooFuture) {
          return {
            ...state,
            orders: state.orders.filter(o => o.id !== order.id),
            movedTripId: order.id,
          };
        } else {
          finalOrders = sortOrdersByStatusAndTime(updatedOrders, filter);
        }
      }

      return {
        ...state,
        orders: finalOrders,
        movedTripId: order.id,
      };
    }

    case "RESET_MOVED_TRIP_ID": {
      return { ...state, movedTripId: "" }
    }

    case "INIT_ORDERS": {
      return { ...state, orders: action.payload };
    }
    case "ADD_ORDERS": {
      const existingIds = new Set((state.orders || []).map(o => o.id));
      const newOrders = (action.payload || []).filter(o => !existingIds.has(o.id));
      return { ...state, orders: [...(state.orders || []), ...newOrders] };
    }
    case "UPDATE_ORDER":
      return {
        ...state,
        orders: state.orders.map((order) => {
          if (order.id_order == action.payload.orderId) {
            order.status = action.payload.status;
            return order;
          }
          return order;
        }),
      };

    case "UPDATE_INCIDENT_ORDER":
      return {
        ...state,
        orders: state.orders.map((order) => {
          if (order.id == action.payload) {
            return { ...order, is_incident: true };
          }
          return order;
        }),
      };
    case "UPDATE_ETA_IN_ORDER":
      return {
        ...state,
        orders: state.orders.map((order) => {
          if (order.id == action.payload.orderId) {
            return { ...order, eta_to_destination: action.payload.eta };
          }
          return order;
        }),
      };
    case "SHOW_ICON_ANIMATION_IN_ORDER":
      return {
        ...state,
        orders: state.orders.map((order) => {
          if (order.id == action.payload.orderId) {
            return { ...order, showUpdatedLogIconAnimation: true };
          }
          return order;
        }),
      };
    case "UPDATE_SINGLE_ORDER": {
      const incoming = action.payload.order;
      const allowInsert = action.payload.allowInsert;
      const isSchedule =
        incoming.status === "scheduled" || incoming.status === "scheduled_accepted";
      const foundOrder = state.orders.find((o) => o.id === incoming.id);

      // The order isn't in the loaded list yet. Insert it (mirroring ADD_ORDER, with
      // the loading-bar animation) only when the caller says it belongs in the current
      // view — otherwise a real-time update for an off-screen trip is dropped and only
      // shows after a manual refresh.
      if (!foundOrder) {
        if (allowInsert) {
          const remainingOrders = (state.orders || []).filter((o) => o.id !== incoming.id);
          const limitedOrders =
            remainingOrders.length >= 100 ? remainingOrders.slice(0, 99) : remainingOrders;
          return {
            ...state,
            orders: [
              { ...incoming, showLoadingBar: true, type: "added" },
              ...limitedOrders,
            ],
          };
        }
        return state;
      }

      if (isSchedule) {
        return {
          ...state,
          orders: [
            { ...foundOrder, ...incoming, showLoadingBar: true, type: "added" },
            ...state.orders.filter((o) => o.id !== incoming.id),
          ],
        };
      }

      return {
        ...state,
        orders: state.orders.map((order) => {
          if (order.id === incoming.id) {
            return {
              ...order,
              ...incoming,
              reposition:
                order.status !== incoming.status ||
                order.schedule_date !== incoming.schedule_date,
              type: "updated",
            };
          }
          return order;
        }),
      };
    }
    case "INIT_NOTIFICATION_BARS": {
      return {
        ...state,
        enRouteNotifications: action.payload.enRoute,
        arrivedNotifications: action.payload.arrived,
        pobNotifications: action.payload.pob,
      };
    }

    case "RESET_LOADING_ANIMATION": {
      return {
        ...state,
        orders: state.orders.map((order) => {

          if (order.id === action.payload) {
            return { ...order, showLoadingBar: false };
          }
          return order;
        }),
      };
    }
    case "UPDATE_FILTER":
      return { ...state, orderFilter: action.payload };

    case "TRIGGER_REFRESH":
      return { ...state, refreshTrigger: state.refreshTrigger + 1 };

    case "SYNC_REALTIME_NOTIFICATIONS": {
      const fullTrip = action.payload;
      const id = fullTrip.id;
      const status = fullTrip.status;

      let newEnRoute = [...(state.enRouteNotifications || [])];
      let newArrived = [...(state.arrivedNotifications || [])];
      let newPob = [...(state.pobNotifications || [])];

      if (status === "started") {
        if (!newEnRoute.find(n => n.id === id)) {
          newEnRoute = [fullTrip, ...newEnRoute];
        }
      }

      if (status === "arrived") {
        newEnRoute = newEnRoute.filter(s => s.id !== id);
        if (!newArrived.find(n => n.id === id)) {
          newArrived = [fullTrip, ...newArrived];
        }
      }

      if (status === "in progress") {
        newArrived = newArrived.filter(s => s.id !== id);
        if (!newPob.find(n => n.id === id)) {
          newPob = [fullTrip, ...newPob];
        }
      }

      if (status === "drop_off") {
        newPob = newPob.filter(s => s.id !== id);
      }

      return {
        ...state,
        enRouteNotifications: newEnRoute,
        arrivedNotifications: newArrived,
        pobNotifications: newPob,
      };
    }

    default:
      return state;
  }
};

export const OrderProvider = ({ children }) => {
  const [state, dispatch] = useReducer(orderReducer, {
    orders: [],
    movedTripId: "",
    enRouteNotifications: [],
    arrivedNotifications: [],
    pobNotifications: [],
    refreshTrigger: 0,
  });

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <OrderContext.Provider value={contextValue}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => useContext(OrderContext);
